import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAllSurveys } from 'api/cansurvey/surveyApi';
import { ROUTE_BASE, ROUTE_CREATE_SURVEY, ROUTE_SURVEY, ROUTE_SURVEY_HOME } from 'constants/routerPath';
import { useNotificationProvider } from 'hooks';

interface Survey {
  SurveyId: number;
  SurveyTitle: string;
  PublicToken?: string;
}

interface UseSurveyInserterOptions {
  /**
   * Inserts the survey link into the active editor. Returns false if the editor
   * wasn't ready, so the hook can surface an error instead of silently no-opping.
   */
  onInsertLink: (link: string) => boolean;
}

export interface UseSurveyInserterResult {
  showPanel: boolean;
  surveys: Survey[];
  selectedSurveyId: number | null;
  loading: boolean;
  showPanelAndLoad: () => void;
  handleSurveyChange: (event: { target: { value: number } }) => void;
  handleEdit: () => void;
  handleCreateNew: () => void;
  handleCancel: () => void;
  handleInsert: () => void;
  reset: () => void;
}

/**
 * Owns the survey-insertion panel: the survey list, the selection, the `?surveyId=`
 * deep link, and building the public survey URL.
 *
 * Deliberately knows nothing about which editor is active — it hands the finished link
 * to `onInsertLink` and lets the caller decide how to place it.
 */
export function useSurveyInserter({ onInsertLink }: UseSurveyInserterOptions): UseSurveyInserterResult {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { notifyError } = useNotificationProvider();

  const [showPanel, setShowPanel] = useState(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState<number | null>(null);
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(false);

  // NOTE: the old component also tracked `selectedSurveyTitle`, but it was never
  // rendered or read anywhere — dropped rather than carried across.

  const fetchSurveys = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all surveys without pagination for the dropdown.
      const data = await getAllSurveys(0, 1000);
      setSurveys(data.surveys);
    } catch (error) {
      console.error('[useSurveyInserter] Failed to fetch surveys:', error);
      notifyError('Failed to load surveys. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [notifyError]);

  // Deep link: ?surveyId= opens the panel with that survey preselected.
  useEffect(() => {
    const surveyIdParam = searchParams.get('surveyId');
    if (!surveyIdParam) return;
    const surveyId = Number(surveyIdParam);
    if (Number.isNaN(surveyId)) return;

    setSelectedSurveyId(surveyId);
    setShowPanel(true);
    fetchSurveys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const showPanelAndLoad = useCallback(() => {
    if (showPanel) return;
    setShowPanel(true);
    if (!selectedSurveyId && surveys.length === 0) {
      fetchSurveys();
    }
  }, [showPanel, selectedSurveyId, surveys.length, fetchSurveys]);

  const handleSurveyChange = useCallback(
    (event: { target: { value: number } }) => {
      const newSurveyId = event.target.value;
      setSelectedSurveyId(newSurveyId);
      setSearchParams({ surveyId: String(newSurveyId) });
    },
    [setSearchParams]
  );

  const handleEdit = useCallback(() => {
    if (selectedSurveyId) {
      navigate(`${ROUTE_CREATE_SURVEY}?surveyId=${selectedSurveyId}`);
    }
  }, [selectedSurveyId, navigate]);

  const handleCreateNew = useCallback(() => {
    navigate(ROUTE_SURVEY_HOME);
  }, [navigate]);

  const handleCancel = useCallback(() => {
    setShowPanel(false);
    setSelectedSurveyId(null);

    const next = new URLSearchParams(searchParams);
    next.delete('surveyId');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const handleInsert = useCallback(() => {
    if (!selectedSurveyId) return;

    const survey = surveys.find((s) => s.SurveyId === selectedSurveyId);
    const surveyLink = survey?.PublicToken
      ? `${window.location.origin}${ROUTE_BASE}${ROUTE_SURVEY}?token=${survey.PublicToken}`
      : `${window.location.origin}${ROUTE_BASE}${ROUTE_SURVEY}?surveyId=${selectedSurveyId}`;

    onInsertLink(surveyLink);
  }, [selectedSurveyId, surveys, onInsertLink]);

  const reset = useCallback(() => {
    setShowPanel(false);
    setSelectedSurveyId(null);
    setSurveys([]);
  }, []);

  return {
    showPanel,
    surveys,
    selectedSurveyId,
    loading,
    showPanelAndLoad,
    handleSurveyChange,
    handleEdit,
    handleCreateNew,
    handleCancel,
    handleInsert,
    reset,
  };
}

export default useSurveyInserter;
